import { Component, inject, Inject, Input, Renderer2, ViewChild } from '@angular/core';
import { Thesaurus } from '@interfaces/thesaurus.interface';
import { ThesaurusEntry } from '@interfaces/thesaurus-entry.interface';
import { ThesaurusEntryComponent } from '@thesaurus-entry/thesaurus-entry.component';
import { ThesaurusService } from '@thesaurus-service/thesaurus.service';
import { ModalComponent } from '@modal/modal.component';
import { TermsService } from '@terms-service/terms.service';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { ThesaurusFooterComponent } from '../thesaurus-footer/thesaurus-footer.component';
import { ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { EventsService } from 'app/services/events/events.service';

@Component({
    selector: 'app-thesaurus-detail',
    templateUrl: './thesaurus-detail.component.html',
    styleUrl: './thesaurus-detail.component.scss',
    standalone: false
})
export class ThesaurusDetailComponent {
  @ViewChild('modal') modal!: ModalComponent;
  @ViewChild('thesEntry') thesEntry!: ThesaurusEntryComponent;
  @ViewChild('thesaurusFooter') thesaurusFooter!: ThesaurusFooterComponent;
  @Input() thesaurus?: Thesaurus;
  @Input() token!: string;
  @Input() term?: string;
  @Input() thesauri?: Thesaurus[];
  @Input() searchTerm?: string;
  @Input() searchOption?: string;
  @Input() searchCategory?: string;
  @Input() searchType?: string;
  closePopup: boolean = true; //Used to determine if the modal should be closed after a thesaurus entry is selected
  appendField: boolean = false;
  targetFieldName: string = ''; //Used to determine the target field for thesaurus entries
  productImage: string = '';
  entries?: ThesaurusEntry[];
  entriesAll?: ThesaurusEntry[];
  offset: number = 0;
  pageCountLabel: string = '50';
  isDisplayEntriesAll: boolean = false;
  subscription: any;
  serviceErrorMsg: string = ""; //Placeholder property in case we decide to customize error messages
  componentContent: string = "entries"; //Content presented by this component
  isCollapsed: boolean = true;
  mainTerm: string = 'main';
  showClass: string = "show";
  collapsingClass: string = 'collapsing';
  collapseClass: string = 'collapse';
  majorType: string = 'major';
  explodeType: string = 'explode';
  isEntriesSearchResults: boolean = false;
  hasAnySuggestedTerms: boolean = false; //Used to determine if the thesaurus service returned any suggested terms
  displayRelationalInstructions: boolean = false; //For Lexicon and Corporation
  eventsService = inject(EventsService);
  allCheckedEntryNames: any;
  

  constructor(
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService,
    private thesaurusService: ThesaurusService,
    public termsService: TermsService,
    private spinnerService: NgxSpinnerService,
    private route: ActivatedRoute,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
  
    if (this.thesaurus?.id && this.term && this.token) {
      this.isDisplayAll(this.term);
      this.eventsService.getShowIndexSpinner().subscribe(() => {
              this.showIndexSpinner()
            });
      this.eventsService.getHideIndexSpinner().subscribe(() => {
              this.hideIndexSpinner();
            });
      if (this.isDisplayEntriesAll) {
        this.termsService.populateEntriesAll(this.thesaurus, this.token, this.offset);
      } else {
        this.termsService.populateEntriesFacet(this.thesaurus, this.term, this.token, this.offset);
      }
    }
    else if(this.thesaurus?.id && this.searchTerm && this.searchOption && this.token) {
      this.isEntriesSearchResults = true;
      this.termsService.populateEntriesSearchTerm(this.thesaurus, this.searchTerm, this.searchOption, this.token, this.offset, this.searchCategory, this.searchType);
    } 
    else if (this.thesaurus?.id && this.searchCategory && this.token) {
      this.termsService.populateEntriesSearchTerm(this.thesaurus, '', '', this.token, this.offset, this.searchCategory, this.searchType);
    }

    this.displayRelationalInstructions = true;
    this.eventsService.emitUpdateProductImageURL(this.route.snapshot.queryParams['amp;productImage'] || '');

    this.termsService.thesaurusEntries$.subscribe(entries => {
      if (this.isDisplayEntriesAll) {
        this.entriesAll = entries;
      } else {
        this.entries = entries;
      }
    });
  }

  displaySuggestedTermsMsg(): boolean {
    return this.termsService.getHasSuggestedTerms() && (this.entries?.length ?? 0) > 0;
  }

  suggestedTermsMessage(): string {
    return this.i18NextService.t('suggestionMessage', { searchTerm : this.searchTerm });
  }

  noResults(): boolean {
    return this.isEntriesSearchResults && !this.termsService.getIsLoading() && ((this.entries?.length ?? 0) == 0);
  }


  //Used to display different ng-templates to the user. If true, it will display the "EntriesAll" template. If false, it will display the "Entries" template (to view terms by individual alpha-numeric characters).
  isDisplayAll(term: string): void {
    (term == "All") ? (this.isDisplayEntriesAll = true) : (this.isDisplayEntriesAll = false);
  }

  onEntryCheckboxChange(entry: ThesaurusEntry, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    console.log(entry);

    this.termsService.updateList(entry.name, this.mainTerm, isChecked, entry.notUnique, entry.paths);

    //When the main checkbox is unchecked, uncheck the Explode and Major checkboxes
    if (!isChecked) {
      this.termsService.updateCheckedState(entry.name, this.explodeType, isChecked);
      this.termsService.updateCheckedState(entry.name, this.majorType, isChecked);
    }
  }

  onEntryExplodeCheckboxChange(entry: ThesaurusEntry, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    console.log(entry);

    //When the Explode checkbox is selected but the "main" checkbox is unchecked, set the "checked" state of the "main" checkbox to true
    if (!this.termsService.isChecked(entry.name) && isChecked) {
      this.termsService.updateList(entry.name, this.mainTerm, isChecked, entry.notUnique, entry.paths);
    }

    this.termsService.updateCheckedState(entry.name, this.explodeType, isChecked);
  }

  onEntryMajorCheckboxChange(entry: ThesaurusEntry, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    console.log(entry);

    //When the Major checkbox is selected but the "main" checkbox is unchecked, set the "checked" state of the "main" checkbox to true
    if (!this.termsService.isChecked(entry.name) && isChecked) {
      this.termsService.updateList(entry.name, this.mainTerm, isChecked, entry.notUnique, entry.paths);
    }
    this.termsService.updateCheckedState(entry.name, this.majorType, isChecked);
  }

  isServiceError(): boolean {
    return this.thesaurusService.isThesaurusServiceError();
  }

  isServiceErrorSource(): boolean {
    return this.thesaurusService.getServiceErrorSource() === this.componentContent;
  }

  isExplode(): boolean {
    if (!this.thesaurus) {
      return false;
    }
    if (this.thesaurus.explode) {
      let thesWithExplode: number = 0;
      if (Array.isArray(this.thesauri) && this.thesauri.length > 1) {
        for (let thes of this.thesauri) {
          if (thes.explode) {
            thesWithExplode++;
          }
        }
        // if more than one thesaurus has explode set to true, don't display the checkbox
        return thesWithExplode == 1 ? true : false;
      }
      else if (this.thesaurus) {
        return this.thesaurus.explode;
      }
    }
    return false;
  }

  isMajor(): boolean {
    return this.thesaurus ? this.thesaurus.major : false;
  }

  isMajorOrExplode(): boolean {
    return this.isExplode() || this.isMajor();
  }

  //To retrieve the appropriate translation key for the #instructions <div>.
  getRelationalTextKey(): string {
    if (this.thesaurus?.id) {
      if (this.thesaurusService.isLexiconThesaurus(this.thesaurus?.id)) {
        return "acta_lexicon_qualifiers_relationalInstructionalText";
      } else if (this.thesaurusService.isCorporationThesaurus(this.thesaurus?.id)) {
        return "company_relationalInstructionalText";
      } else if (this.thesaurusService.isThesaurusWithRoot(this.thesaurus?.id)) {
        return "hierarchicalInstructionalText";
      }
    }
    return 'relationalInstructionalText';
  }

  //When a collapsing-note is opened, close any other collapsing-note that might be open.
  closeOpenCollapseElements(allCollapseElements: NodeList, collapseId: string) {
    allCollapseElements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      if (htmlElement.id !== collapseId) {
        this.renderer.removeClass(htmlElement, this.showClass);
        this.renderer.addClass(htmlElement, this.collapsingClass);
        setTimeout(() => {
          this.renderer.removeClass(htmlElement, this.collapsingClass);
          this.renderer.addClass(htmlElement, this.collapseClass);
        }, 10);
      }
    });
  }
  
  showDetailSpinner(spinnerName: string) {
    this.spinnerService.show(spinnerName);
    this.eventsService.emitDisableHelpButtonEvent(true);
  }

  hideDetailSpinner(spinnerName: string) {
    this.spinnerService.hide(spinnerName);
    this.eventsService.emitDisableHelpButtonEvent(false);
  }
  
   showIndexSpinner() {
    this.spinnerService.show("index-spinner");
    this.eventsService.emitDisableHelpButtonEvent(true);
  }

  hideIndexSpinner() {
    this.spinnerService.hide("index-spinner");
    this.eventsService.emitDisableHelpButtonEvent(false);
  }

  anyEntriesToDisplay(): boolean {
    if (this.isDisplayEntriesAll) {
      return (this.entriesAll && this.entriesAll.length > 0) ? true : false;
    } else {
      return (this.entries && this.entries.length > 0) ? true : false;
    }
  }
}
